import React from 'react';
import { AlertTriangle, TrendingUp, ShieldAlert, Check, AlertCircle, BarChart2, Building, ArrowUpRight, ArrowDownRight, Calculator } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов оценки риска банкротства
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Функция для определения цвета индикатора риска
  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'низкий':
      case 'очень низкий':
        return 'green';
      case 'умеренный':
        return 'yellow';
      case 'высокий':
        return 'orange';
      case 'очень высокий':
      case 'критический':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Функция для получения названия модели
  const getModelName = (modelId) => {
    const models = {
      'altman-z': 'Модель Альтмана (Z-счет)',
      'fulmer': 'Модель Фулмера',
      'springate': 'Модель Спрингейта',
      'taffler': 'Модель Таффлера',
      'beaver': 'Модель Бивера',
      'saifullin-kadykov': 'Модель Сайфуллина-Кадыкова',
      'comprehensive': 'Комплексная оценка'
    };
    return models[modelId] || modelId;
  };

  // Функция для форматирования финансовых индикаторов
  const formatFinancialIndicator = (value, includeSign = true) => {
    const prefix = includeSign && value > 0 ? '+' : '';
    return `${prefix}${formatNumber(value, 2)}`;
  };

  // Определение основного цвета на основе уровня риска
  const riskColor = getRiskColor(results.riskLevel.toLowerCase());

  return (
    <ToolResult title="Результаты оценки риска банкротства">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Уровень риска */}
        <div className={`rounded-lg bg-gradient-to-br from-${riskColor}-50 to-${riskColor}-100 dark:from-${riskColor}-900/30 dark:to-${riskColor}-800/30 p-5 border border-${riskColor}-200 dark:border-${riskColor}-800/50 shadow-sm`}>
          <h3 className={`text-base font-medium text-${riskColor}-700 dark:text-${riskColor}-300 mb-1.5 flex items-center gap-1.5`}>
            <ShieldAlert className="w-4 h-4"/>
            Уровень риска банкротства
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold text-${riskColor}-800 dark:text-${riskColor}-200`} aria-live="polite">
              {results.riskLevel}
            </span>
          </div>
          <p className={`text-xs text-${riskColor}-600 dark:text-${riskColor}-400 mt-1`}>
            {results.interpretation}
          </p>
        </div>

        {/* Интегральный показатель */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <BarChart2 className="w-4 h-4"/>
            Оценка по {getModelName(results.calculationModel)}
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatNumber(results.score, 2)}
            </span>
            {results.thresholds && (
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                (пороговое значение: {formatNumber(results.thresholds.critical || results.thresholds.main, 2)})
              </span>
            )}
          </div>
          <div className="mt-2">
            <div className={`risk-indicator risk-indicator-${riskColor}`}></div>
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Используемая модель" 
          value={getModelName(results.calculationModel)} 
          color="blue" 
          icon={<Calculator className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Тип организации" 
          value={results.companyType === 'llc' ? 'Общество с ограниченной ответственностью (ООО)' : 
                 results.companyType === 'jsc' ? 'Акционерное общество (АО)' :
                 results.companyType === 'individual' ? 'Индивидуальный предприниматель' :
                 results.companyType === 'partnership' ? 'Товарищество' :
                 results.companyType === 'cooperative' ? 'Кооператив' :
                 results.companyType === 'nonprofit' ? 'Некоммерческая организация' : results.companyType} 
          icon={<Building className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Отрасль деятельности" 
          value={results.industryType === 'manufacturing' ? 'Производство' :
                 results.industryType === 'retail' ? 'Розничная торговля' :
                 results.industryType === 'wholesale' ? 'Оптовая торговля' :
                 results.industryType === 'services' ? 'Услуги' :
                 results.industryType === 'construction' ? 'Строительство' :
                 results.industryType === 'agriculture' ? 'Сельское хозяйство' :
                 results.industryType === 'it' ? 'Информационные технологии' :
                 results.industryType === 'finance' ? 'Финансы и страхование' :
                 results.industryType === 'hospitality' ? 'Гостиничный бизнес и общепит' :
                 results.industryType === 'transportation' ? 'Транспорт и логистика' :
                 results.industryType === 'healthcare' ? 'Здравоохранение' :
                 results.industryType === 'realestate' ? 'Недвижимость' : results.industryType}
          color="gray" 
        />
        
        {results.includesQualitativeFactors && (
          <ResultItem 
            label="Качественные факторы" 
            value="Учтены в оценке" 
            color="orange" 
            icon={<Check className="w-4 h-4"/>} 
          />
        )}
        
        {/* Ключевые коэффициенты */}
        {results.ratios && Object.keys(results.ratios).length > 0 && (
          <>
            <div className="pt-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ключевые финансовые коэффициенты:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(results.ratios).map(([key, value], index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-800/50 rounded p-2 border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600 dark:text-gray-400">{key}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatFinancialIndicator(value, false)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        
        {/* Рекомендации */}
        {results.recommendations && results.recommendations.length > 0 && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mt-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
              <TrendingUp className="w-4 h-4 mr-2 text-green-500" />
              Рекомендации для снижения риска банкротства
            </h4>
            <ul className="mt-2 space-y-2">
              {results.recommendations.map((recommendation, index) => (
                <li key={index} className="flex items-start">
                  <ArrowUpRight className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Предупреждение о точности */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 mt-2 border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Важно помнить</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Данная оценка риска банкротства основана на математических моделях и имеет ориентировочный характер. Точность прогноза зависит от достоверности предоставленных данных, отраслевой специфики и других факторов. Рекомендуется использовать результаты как часть комплексного финансового анализа.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;